import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Shield, ArrowLeft, Loader2 } from 'lucide-react'
import { PLANS } from '@/utils/plans'
import { useAuthStore } from '@/store/authStore'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'
import toast from 'react-hot-toast'

export default function UpgradePlanPage() {
    const navigate = useNavigate()
    const { currentOrg } = useAuthStore()
    const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null)

    const handleSubscribe = async (planId: string) => {
        if (!currentOrg) return

        try {
            setLoadingPlanId(planId)
            const createSubscription = httpsCallable(functions, 'createMercadoPagoSubscription')

            // Chama a Cloud Function para gerar o link personalizado
            const response = await createSubscription({
                planId,
                orgId: currentOrg.id
            }) as any

            const { initPoint } = response.data

            if (initPoint) {
                // Redireciona para o checkout do MP
                window.location.href = initPoint
            } else {
                throw new Error('Link de pagamento não recebido.')
            }

        } catch (error: any) {
            console.error('Erro ao assinar:', error)
            toast.error('Erro ao iniciar assinatura. Tente novamente.')
        } finally {
            setLoadingPlanId(null)
        }
    }

    return (
        <div className="max-w-7xl mx-auto p-6 md:p-12 font-sans animate-in fade-in duration-500">
            <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center text-gray-400 hover:text-gray-900 font-black uppercase text-[10px] tracking-widest transition-colors mb-8"
            >
                <ArrowLeft className="w-4 h-4 mr-2" /> Voltar para Dashboard
            </button>

            <div className="text-center mb-16 space-y-4">
                <h1 className="text-4xl md:text-5xl font-black text-gray-900 uppercase tracking-tighter leading-none">
                    Escolha o seu <span className="text-primary">Plano</span>
                </h1>
                <p className="text-gray-500 font-medium max-w-2xl mx-auto">
                    Aumente seus limites e desbloqueie recursos premium para levar sua gestão de espaços para o próximo nível.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {Object.values(PLANS).map((plan) => {
                    const isCurrent = currentOrg?.plan === plan.id
                    const isRecommended = plan.recommended
                    const isLoading = loadingPlanId === plan.id

                    return (
                        <div
                            key={plan.id}
                            className={`relative bg-white rounded-[2.5rem] p-8 border-2 flex flex-col transition-all duration-300 hover:-translate-y-2
                                ${isCurrent ? 'border-green-500 ring-4 ring-green-500/10' :
                                    isRecommended ? 'border-primary shadow-2xl shadow-primary/20 scale-105 z-10' : 'border-gray-100 hover:border-gray-200 hover:shadow-xl'}
                            `}
                        >
                            {isCurrent && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                                    Plano Atual
                                </div>
                            )}
                            {isRecommended && !isCurrent && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                                    Recomendado
                                </div>
                            )}

                            <div className="mb-6 space-y-2 text-center">
                                <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight">{plan.name}</h3>
                                <div className="flex items-baseline justify-center">
                                    <span className="text-sm font-bold text-gray-400">R$</span>
                                    <span className="text-4xl font-black text-gray-900 tracking-tighter">{plan.price.toFixed(2).replace('.', ',')}</span>
                                    <span className="text-sm font-bold text-gray-400">/mês</span>
                                </div>
                            </div>

                            <ul className="space-y-4 mb-8 flex-grow">
                                {plan.features.map((feature, i) => (
                                    <li key={i} className="flex items-start text-sm text-gray-600 font-medium leading-tight">
                                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => handleSubscribe(plan.id)}
                                disabled={isCurrent || plan.price === 0 || !!loadingPlanId}
                                className={`w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center
                                    ${isCurrent
                                        ? 'bg-green-50 text-green-600 cursor-default'
                                        : isRecommended
                                            ? 'bg-primary text-secondary hover:bg-secondary hover:text-primary shadow-lg shadow-primary/30'
                                            : 'bg-gray-900 text-white hover:bg-gray-800'}
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                `}
                            >
                                {isLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    isCurrent ? 'Seu Plano' : plan.price === 0 ? 'Plano Gratuito' : 'Assinar Agora'
                                )}
                            </button>
                        </div>
                    )
                })}
            </div>

            <div className="mt-20 bg-gray-50 rounded-[3rem] p-12 text-center space-y-6">
                <Shield className="w-12 h-12 text-gray-300 mx-auto" />
                <div className="space-y-2">
                    <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Pagamento Seguro</h3>
                    <p className="text-sm text-gray-500 font-medium max-w-lg mx-auto">
                        Seus pagamentos são processados pelo Mercado Pago com criptografia de ponta a ponta.
                        Você pode cancelar sua assinatura a qualquer momento.
                    </p>
                </div>
            </div>
        </div>
    )
}
